import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password2: '',
    company_name: '',
    contact_name: '',
    country: 'Cyprus',
    team_size: '',
    num_projects: 0,
    agree_terms: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }

    if (generalError) {
      setGeneralError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 10) {
      newErrors.password = 'Password must be at least 10 characters';
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = 'Password must have an uppercase letter';
    } else if (!/[a-z]/.test(formData.password)) {
      newErrors.password = 'Password must have a lowercase letter';
    } else if (!/[0-9]/.test(formData.password)) {
      newErrors.password = 'Password must have a number';
    } else if (!/[!@#$%^&*()_+\-=]/.test(formData.password)) {
      newErrors.password =
        'Password must have a special character (!@#$%^&*)';
    }

    if (formData.password !== formData.password2) {
      newErrors.password2 = 'Passwords do not match';
    }

    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }

    if (!formData.contact_name.trim()) {
      newErrors.contact_name = 'Contact name is required';
    }

    if (!formData.agree_terms) {
      newErrors.agree_terms = 'You must agree to the Terms of Service and Privacy Policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await register(formData);
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      if (error.message) {
        setGeneralError(error.message);
      } else {
        setGeneralError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (fieldName) =>
    `w-full px-4 py-3 bg-neutral-900 border ${
      errors[fieldName] ? 'border-red-500' : 'border-neutral-700'
    } text-white placeholder-neutral-500 focus:outline-none focus:border-[#9fc6ff] transition-colors`;

  return (
    <div className="min-h-screen bg-[#05070d] flex flex-col relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Decorative glow */}
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-blue-300/10 rounded-full blur-3xl pointer-events-none"></div>

      {/* Navigation */}
      <nav className="relative z-10 py-6 px-6 lg:px-12 border-b border-white/10 bg-[#05070d]/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={logo}
              alt="RAL Real Advisor"
              className="w-10 h-10 object-contain"
            />
            <div className="flex items-center">
              <span className="text-2xl font-serif font-bold text-white">RAL</span>
              <span className="text-2xl font-serif font-light text-[#9fc6ff] ml-1">
                Real Advisor
              </span>
            </div>
          </Link>

          <Link
            to="/login"
            className="text-sm font-medium tracking-wide text-white/90 hover:text-[#9fc6ff] transition-colors"
          >
            Sign In
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-lg">
          {/* Header */}
          <div className="text-center mb-10">
            <span className="text-[#9fc6ff] text-sm font-medium tracking-[0.3em] uppercase mb-4 block">
              Get Started
            </span>
            <h1 className="text-4xl font-serif font-bold text-white mb-3">
              Create Account
            </h1>
            <p className="text-neutral-400">
              Create your account to access the platform
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-neutral-800/50 backdrop-blur border border-neutral-700/50 p-8">
            {/* General Error */}
            {generalError && (
              <div className="mb-6 p-4 bg-red-900/20 border border-red-700/40">
                <p className="text-red-300 text-sm">{generalError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Account Details Section */}
              <div className="pb-6 border-b border-neutral-700">
                <h2 className="text-lg font-serif font-bold text-white mb-6">
                  Account Details
                </h2>

                {/* Email */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-neutral-300 mb-2 tracking-wide uppercase">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClass('email')}
                    placeholder="your@email.com"
                  />
                  {errors.email && (
                    <p className="text-red-400 text-xs mt-2">{errors.email}</p>
                  )}
                </div>

                {/* Password */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-neutral-300 mb-2 tracking-wide uppercase">
                    Password *
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={inputClass('password')}
                    placeholder="Min 10 characters"
                  />
                  {errors.password && (
                    <p className="text-red-400 text-xs mt-2">{errors.password}</p>
                  )}
                  <p className="text-neutral-500 text-xs mt-2">
                    Must include: uppercase, lowercase, number, special
                    character
                  </p>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2 tracking-wide uppercase">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    name="password2"
                    value={formData.password2}
                    onChange={handleChange}
                    className={inputClass('password2')}
                    placeholder="Repeat your password"
                  />
                  {errors.password2 && (
                    <p className="text-red-400 text-xs mt-2">
                      {errors.password2}
                    </p>
                  )}
                </div>
              </div>

              {/* Company Details Section */}
              <div>
                <h2 className="text-lg font-serif font-bold text-white mb-6">
                  Company Details
                </h2>

                {/* Company Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-neutral-300 mb-2 tracking-wide uppercase">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    className={inputClass('company_name')}
                    placeholder="Your company name"
                  />
                  {errors.company_name && (
                    <p className="text-red-400 text-xs mt-2">
                      {errors.company_name}
                    </p>
                  )}
                </div>

                {/* Contact Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-neutral-300 mb-2 tracking-wide uppercase">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleChange}
                    className={inputClass('contact_name')}
                    placeholder="Your full name"
                  />
                  {errors.contact_name && (
                    <p className="text-red-400 text-xs mt-2">
                      {errors.contact_name}
                    </p>
                  )}
                </div>

                {/* Country */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-neutral-300 mb-2 tracking-wide uppercase">
                    Country
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 text-white focus:outline-none focus:border-[#9fc6ff] transition-colors"
                  >
                    <option value="Cyprus">Cyprus</option>
                    <option value="Greece">Greece</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="United Arab Emirates">United Arab Emirates</option>
                    <option value="Germany">Germany</option>
                    <option value="France">France</option>
                    <option value="Russia">Russia</option>
                    <option value="China">China</option>
                    <option value="Israel">Israel</option>
                    <option value="Lebanon">Lebanon</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Team Size & Projects */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2 tracking-wide uppercase">
                      Team Size
                    </label>
                    <select
                      name="team_size"
                      value={formData.team_size}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 text-white focus:outline-none focus:border-[#9fc6ff] transition-colors"
                    >
                      <option value="">Select...</option>
                      <option value="1-5">1-5</option>
                      <option value="6-10">6-10</option>
                      <option value="11-25">11-25</option>
                      <option value="26-50">26-50</option>
                      <option value="50+">50+</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2 tracking-wide uppercase">
                      Active Projects
                    </label>
                    <input
                      type="number"
                      name="num_projects"
                      value={formData.num_projects}
                      onChange={handleChange}
                      min="0"
                      className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 text-white placeholder-neutral-500 focus:outline-none focus:border-[#9fc6ff] transition-colors"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {/* Terms Agreement */}
              <div>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="agree_terms"
                    checked={formData.agree_terms}
                    onChange={handleChange}
                    className="mt-1 w-4 h-4 accent-[#9fc6ff] bg-neutral-900 border-neutral-700"
                  />

                  <label className="text-neutral-400 text-sm">
                    I agree to the{' '}
                    <Link
                      to="/terms"
                      className="text-[#9fc6ff] hover:text-[#cfe2ff] transition-colors"
                    >
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link
                      to="/privacy"
                      className="text-[#9fc6ff] hover:text-[#cfe2ff] transition-colors"
                    >
                      Privacy Policy
                    </Link>
                  </label>
                </div>

                {errors.agree_terms && (
                  <p className="text-red-400 text-xs mt-2">{errors.agree_terms}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !formData.agree_terms}
                className="w-full py-4 bg-[#9fc6ff] text-[#05070d] font-medium tracking-wide uppercase hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>

            {/* Login Link */}
            <p className="mt-8 text-center text-neutral-400">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-[#9fc6ff] hover:text-[#cfe2ff] font-medium transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-neutral-600 text-xs">
              Your account can only be created after accepting the Terms of
              Service and Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;